import { SetStateAction, useEffect, useRef, useState } from "react";
import { FormReservation } from "./form-reservation";
import toast from "react-hot-toast";
import { fetchReservation, Reservation } from "services/fetch-reservation";
import { Formatter } from "utils/formatters";

export const LayoutUserReservation = () => {
  const [selectedOption, setSelectedOption] = useState("confirmed");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const dropdownRef = useRef(null);

  const options = [
    { value: 'confirmed', label: 'Confirmadas' },
    { value: 'cancelled', label: 'Canceladas' }
  ];

  const handleOptionClick = (value: SetStateAction<string>) => {
    setSelectedOption(value);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((dropdownRef.current as unknown as HTMLElement) && !(dropdownRef.current as unknown as HTMLElement).contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderTableRows = () => {
    const filteredReservations = reservations
      .filter((reservation) => {
        return reservation.status.toLowerCase() === selectedOption;
      })
      .filter((reservation) => {
        const startDateMatches = startDateFilter
          ? new Date(reservation.checkin_date) >= new Date(Formatter.convertDateBrasilForEua(startDateFilter))
          : true;
        const endDateMatches = endDateFilter
          ? new Date(reservation.checkout_date) <= new Date(Formatter.convertDateBrasilForEua(endDateFilter))
          : true;

        return startDateMatches && endDateMatches;
      })
      .sort((a, b) => {
        if (a.broker_service_id < b.broker_service_id) {
          return 1;
        } else if (a.broker_service_id > b.broker_service_id) {
          return -1;
        }
        return 0;
      });

    if (!isLoading && filteredReservations.length === 0) {
      return (
        <div className="flex flex-col text-center items-center justify-center mx-auto w-full h-40">
          <h1 className="text-2xl">Nenhuma reserva encontrada</h1>
        </div>
      );
    }

    return (
      <table className="table-auto w-full">
        <thead className="hidden md:table-header-group">
          <tr className="">
            <th className="text-center text-lg font-bold text-neutral-700 px-4 py-2 dark:text-white">
              Reserva ID
            </th>
            <th className="text-center text-lg font-bold text-neutral-700 px-4 py-2 dark:text-white">
              Check-in
            </th>
            <th className="text-center text-lg font-bold text-neutral-700 px-4 py-2 dark:text-white">
              Check-out
            </th>
            {
              selectedOption !== "cancelled" && (
                <th className="text-center text-lg font-bold text-neutral-700 px-4 py-2 dark:text-white">
                  Ações
                </th>
              )
            }
          </tr>
        </thead>
        <tbody className="w-full flex flex-col gap-5 md:table-row-group">
          {filteredReservations.map((reservation) => (
            <FormReservation
              key={reservation.broker_service_id}
              dateCheckin={reservation.checkin_date}
              dateCheckout={reservation.checkout_date}
              reservationId={reservation.broker_reservation_id}
              serviceId={reservation.broker_service_id}
              isCancelled={selectedOption === "cancelled"}
              linkVoucherReservation={reservation.voucher_url}
              cancellationPolicies={reservation.cancellation_policies}
              bookingId={reservation.id}
            />
          ))}
        </tbody>
      </table>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetchReservation();
        setReservations(response);
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message || "Erro ao buscar suas reservas");
        } else {
          toast.error("Erro ao buscar suas reservas");
        }

        setReservations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="flex flex-col items-center w-full">
      <main className="my-10 flex flex-col w-full">
        <div className="flex flex-col gap-6 w-full">
          <form className="flex md:flex-row flex-col gap-4 items-center justify-center mb-6 md:mb-auto mx-auto w-full md:w-4/6">
            <div className="relative w-full md:w-auto" ref={dropdownRef}>
              <button
                type="button"
                className="
                  w-full h-12 border border-neutral-200 dark:border-neutral-700 
                  rounded-full px-4 hover:border-blue focus:border-blue
                  flex items-center justify-between cursor-pointer
                "
                onClick={() => setIsOpen(!isOpen)}
              >
                <p className="text-black dark:text-white">{options.find(option => option.value === selectedOption)?.label}</p>
                <svg
                  className="w-5 h-5 fill-current text-gray-700 ml-2"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M10 12l-4-4h8l-4 4z" />
                </svg>
              </button>
              {isOpen && (
                <div className="
                  absolute z-10 mt-2 w-full bg-white dark:bg-neutral-900
                  shadow-lg rounded-2xl border-[0.05rem] border-neutral-200/70 
                  overflow-hidden dark:border-neutral-700
                ">
                  {options.map(option => (
                    <button
                      type="button"
                      key={option.value}
                      className="px-4 py-2 w-full hover:bg-gray-100 cursor-pointer hover:text-black dark:hover:bg-neutral-800 dark:hover:text-white"
                      onClick={() => handleOptionClick(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex md:flex-row flex-col gap-4 w-full">
              <input
                className="md:w-1/2 h-12 border border-neutral-200 rounded-full px-4 hover:border-blue focus:border-blue"
                type="text"
                placeholder="Data de Check-in"
                onFocus={(e) => e.target.type = "date"}
                onBlur={(e) => {
                  e.target.type = "text";
                  if (e.target.value) {
                    setStartDateFilter(Formatter.prettyDateBRFormated(e.target.value))
                  }
                }}
                name="startDate"
                value={startDateFilter}
                onChange={(e) => {
                  setStartDateFilter(e.target.value)
                }}
              />
              <input
                className="md:w-1/2 h-12 border border-neutral-200 rounded-full px-4 hover:border-blue focus:border-blue"
                type="text"
                onFocus={(e) => e.target.type = "date"}
                onBlur={(e) => {
                  e.target.type = "text";
                  if (e.target.value) {
                    setEndDateFilter(Formatter.prettyDateBRFormated(e.target.value))
                  }
                }}
                placeholder="Data de Check-out"
                name="endDate"
                value={endDateFilter}
                onChange={(e) => {
                  setEndDateFilter(e.target.value)
                }}
              />
            </div>
            <button
              type="button"
              className="text-left text-sm w-full ml-2 md:ml-auto text-neutral-500 justify-start md:w-1/5 md:text-center underline"
              onClick={() => {
                if (startDateFilter || endDateFilter) {
                  setStartDateFilter("");
                  setEndDateFilter("");
                }
              }}
            >
              Limpar Filtro
            </button>
          </form>
          {isLoading && (
            <div className="flex flex-col items-center justify-center w-full h-80">
              <h1 className="text-2xl">Carregando...</h1>
            </div>
          )}
          {!isLoading && renderTableRows()}
        </div>
      </main>
    </div>
  );
};